import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';

import {
  EntrySummaryCountByStatusModel,
  EntrySummaryDutiesModel,
  EntrySummaryModel,
  EntrySummaryShortModel,
} from '@data-layer/models/entry-summaries';
import { BondModel, PaginatedResultModel, ShortModel } from '@data-layer/models/common';
import { enumOcrStatus, enumPlatformType } from '@data-layer/enums/common';
import { InternalNote } from '@shared/internal-notes/interfaces';
import { enumEntrySummaryState } from '@data-layer/enums/entry-summaries';
import { enumMarineInsuranceStatusType } from '../enums/marine-insurance/enumInsuranceStatusType.enum';

@Injectable({
  providedIn: 'root',
})
export class EntrySummaryRepository {
  constructor(private apiService: ApiService) {}

  // TODO refactor params & filters to be more generic
  public getList(companyId: number, offset: number, limit: number, filters: any): Observable<PaginatedResultModel<EntrySummaryShortModel>> {
    return this.apiService.get<PaginatedResultModel<EntrySummaryShortModel>>(`${environment.url.companies}${companyId}/entry-summaries`, {
      params: { offset, limit, ...filters },
    });
  }

  public getEntrySummary(referenceNumber: string): Observable<EntrySummaryModel> {
    return this.apiService.get<EntrySummaryModel>(`${environment.url.prefixCustoms}v2/entry-summaries/${referenceNumber}`);
  }

  public getState(id: number): Observable<enumEntrySummaryState> {
    return this.apiService.get<enumEntrySummaryState>(`${environment.url.prefixCustoms}v2/entry-summaries/${id}/state`, null, {
      isShowLoader: false,
    });
  }

  public create(companyId: number, entrySummary: Partial<EntrySummaryModel>): Observable<EntrySummaryModel> {
    return this.apiService.post<EntrySummaryModel>(`${environment.url.companies}${companyId}/entry-summaries`, entrySummary);
  }

  public update(entrySummaryId: number, entrySummary: Partial<EntrySummaryModel>): Observable<EntrySummaryModel> {
    return this.apiService.put<EntrySummaryModel>(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}`, entrySummary);
  }

  public updatePlatform(entrySummaryId: number, platformType: enumPlatformType): Observable<EntrySummaryModel> {
    return this.apiService.put<EntrySummaryModel>(
      `${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/platformType`,
      platformType,
      null,
      { isShowLoader: false },
    );
  }

  public updateInsuranceStatus(esId: number, status: enumMarineInsuranceStatusType): Observable<never> {
    return this.apiService.post(`${environment.url.esInsurance}${esId}/marine-insurance/status`, status);
  }

  public duplicate(entrySummaryId: number): Observable<EntrySummaryModel> {
    return this.apiService.post<EntrySummaryModel>(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}`);
  }

  public delete(id: number): Observable<never> {
    return this.apiService.delete(`${environment.url.prefixCustoms}v2/entry-summaries/${id}`);
  }

  public updateBond(entrySummaryId: number, bond: BondModel): Observable<never> {
    return this.apiService.put(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/bond`, bond);
  }

  public updateInternalNote(entrySummaryId: number, data: InternalNote): Observable<void> {
    return this.apiService.put(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/internal-note`, data);
  }

  public connectBondRequest(entrySummaryId: number, bondRequestId: number): Observable<never> {
    return this.apiService.put(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/bond-request-id`, bondRequestId);
  }

  public disconnectBondRequest(entrySummaryId: number): Observable<never> {
    return this.apiService.delete(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/bond-request-id`);
  }

  public getDuties(entrySummaryId: number): Observable<EntrySummaryDutiesModel> {
    return this.apiService.get<EntrySummaryDutiesModel>(
      `${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/duties`,
      null,
      {
        isShowLoader: false,
      },
    );
  }

  public getConnectedEntrySummaries(isfId: number): Observable<ShortModel[]> {
    return this.apiService.get<ShortModel[]>(`${environment.url.prefixCustoms}v1/isfs/${isfId}/connected-entry-summaries`);
  }

  public getEsCountStats(companyId: number): Observable<EntrySummaryCountByStatusModel[]> {
    return this.apiService.get<EntrySummaryCountByStatusModel[]>(
      `${environment.url.prefixCustoms}v2/companies/${companyId}/es-count-by-status`,
    );
  }

  public setConnectedIsf(entrySummaryId: string, isfId: string): Observable<ShortModel> {
    return this.apiService.put<ShortModel>(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/isf`, `"${isfId}"`);
  }

  public getValidationStatuses(companyId: number, referenceNumbers: string[]): Observable<{ [name: string]: boolean }> {
    return this.apiService.get<{ [name: string]: boolean }>(`${environment.url.prefixCustoms}v2/entry-summaries/validation-statuses`, {
      params: { referenceNumbers },
    });
  }

  public getOcrStatus(entrySummaryId: number, isShowLoader: boolean = false): Observable<enumOcrStatus> {
    return this.apiService.get(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/ocr-status`, null, { isShowLoader });
  }

  public resetOcrStatus(entrySummaryId: number): Observable<enumOcrStatus> {
    return this.apiService.delete(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/ocr`);
  }

  public ocrProcessing(entrySummaryId: number): Observable<void> {
    return this.apiService.post<void>(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/ocr`);
  }
}
